import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Receipt, Printer, Mail, Download, Pencil, AlertCircle,
  Building2, User as UserIcon, Calendar, CreditCard, Hash,
  CheckCircle2, RotateCcw, FileText, MessageSquare, Eye,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppButton, AppModal, AppCard, AppCardContent,
  UserAvatar, EmptyState,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import FilePreviewModal from "../Common/FilePreviewModal";
import "./fee-details.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);
}

function formatDate(d, withTime = false) {
  if (!d) return "—";
  const date = new Date(d);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    timeZone: "Asia/Kolkata",
  });
}

const STATUS_META = {
  success:             { label: "Paid",            cls: "fd-status--paid",    Icon: CheckCircle2 },
  pending:             { label: "Pending",         cls: "fd-status--pending", Icon: AlertCircle },
  failed:              { label: "Failed",          cls: "fd-status--failed",  Icon: AlertCircle },
  refunded:            { label: "Refunded",        cls: "fd-status--refund",  Icon: RotateCcw },
  partially_refunded:  { label: "Partial Refund",  cls: "fd-status--refund",  Icon: RotateCcw },
};

const FeeDetailsPage = () => {
  const { id, feeId } = useParams();
  const navigate = useNavigate();
  const realFeeId = feeId || id;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const [downloading, setDownloading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const [showImage, setShowImage] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  // Fetch fee details
  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiClient.get(`/fees/${realFeeId}`)
      .then(({ data: payload }) => {
        if (!alive) return;
        setData(payload);
        setEmailValue(payload?.transaction?.studentId?.email || "");
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.response?.data?.message || "Failed to load fee details.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [realFeeId]);

  const txn = data?.transaction;
  const institute = data?.institute;
  const invoiceNumber = data?.invoiceNumber;
  const student = txn?.studentId;
  const courseName =
    txn?.courseAssignmentId?.courseId?.name ||
    student?.course?.name ||
    "—";
  const batchName =
    txn?.courseAssignmentId?.batchId?.name ||
    student?.batch?.name ||
    "";

  const status = STATUS_META[txn?.paymentStatus] || STATUS_META.success;
  const StatusIcon = status.Icon;
  const isOnline = txn?.paymentGateway === "razorpay";
  const txnRef = txn?.razorpayPaymentId || txn?.referenceNumber || "—";
  const refundedAmount = Number(txn?.refundAmount || 0);
  const netPaid = Math.max(0, Number(txn?.currentPayment || 0) - refundedAmount);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleDownload = useCallback(async () => {
    if (!realFeeId) return;
    setDownloading(true);
    try {
      const res = await apiClient.get(`/fees/${realFeeId}/invoice`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${invoiceNumber || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setToast({
        show: true,
        message: e?.response?.data?.message || "Failed to download invoice.",
        type: "danger",
      });
    }
    setDownloading(false);
  }, [realFeeId, invoiceNumber]);

  const handleSend = useCallback(async () => {
    setSendError("");
    const email = (emailValue || "").trim();
    if (!email) { setSendError("Email is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSendError("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const { data: out } = await apiClient.post(`/fees/${realFeeId}/send-invoice`, { email });
      setShowSendModal(false);
      setToast({
        show: true,
        message: out?.message || `Invoice sent to ${email}`,
        type: "success",
      });
    } catch (e) {
      setSendError(e?.response?.data?.message || "Failed to send invoice. Please try again.");
    }
    setSending(false);
  }, [emailValue, realFeeId]);

  const handleViewProof = useCallback(async () => {
    if (!txn?.refImage) return;
    setShowImage(true);
    setImageLoading(true);
    setImageUrl(null);
    try {
      const { data: r } = await apiClient.get(
        `/files/presign-get?key=${encodeURIComponent(txn.refImage)}`
      );
      setImageUrl(r.url);
    } catch {
      setImageUrl(null);
    }
    setImageLoading(false);
  }, [txn]);

  const headerActions = useMemo(() => (
    <div className="fd-header-actions no-print">
      <AppButton variant="secondary" onClick={handlePrint} disabled={loading || !txn}>
        <Printer size={16} />
        <span className="fd-btn-label">Print</span>
      </AppButton>
      <AppButton variant="secondary" onClick={handleDownload} disabled={loading || !txn || downloading}>
        {downloading ? <Spinner animation="border" size="sm" /> : <Download size={16} />}
        <span className="fd-btn-label">Download PDF</span>
      </AppButton>
      <AppButton onClick={() => { setSendError(""); setShowSendModal(true); }} disabled={loading || !txn}>
        <Mail size={16} />
        <span className="fd-btn-label">Send Invoice</span>
      </AppButton>
    </div>
  ), [handlePrint, handleDownload, loading, txn, downloading]);

  // ─── Render states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fd-page">
        <div className="fd-loading">
          <Spinner animation="border" />
          <span>Loading invoice…</span>
        </div>
      </div>
    );
  }

  if (err || !txn) {
    return (
      <div className="fd-page">
        <div className="fd-back-row no-print">
          <PageHeader
            title="Fee Invoice Details"
            icon={Receipt}
            backButton
          />
        </div>
        <AppCard>
          <AppCardContent>
            <EmptyState
              icon={<AlertCircle size={40} />}
              title="Fee record not found"
              description={err || "We couldn't load this fee transaction."}
            />
          </AppCardContent>
        </AppCard>
      </div>
    );
  }

  return (
    <div className="fd-page">
      {/* ── Header with actions (hidden on print) ── */}
      <div className="no-print">
        <PageHeader
          title="Fee Invoice Details"
          description={`Invoice ${invoiceNumber}`}
          icon={Receipt}
          actions={headerActions}
          backButton
        />
      </div>

      {/* ── Invoice card (printable area) ── */}
      <div className="fd-invoice-wrap">
        <article className="fd-invoice" id="fd-printable">

          {/* Top band */}
          <header className="fd-invoice__top">
            <div className="fd-invoice__brand">
              <div className="fd-invoice__brand-mark">
                <Building2 size={22} />
              </div>
              <div className="fd-invoice__brand-text">
                <h2 className="fd-invoice__brand-name">{institute?.name || "Institute"}</h2>
                {institute?.address && (
                  <p className="fd-invoice__brand-line">{institute.address}</p>
                )}
                <p className="fd-invoice__brand-line">
                  {institute?.email && <span>{institute.email}</span>}
                  {institute?.phone && <span> · {institute.phone}</span>}
                </p>
                {institute?.gstNumber && (
                  <p className="fd-invoice__brand-line fd-invoice__brand-gst">
                    GSTIN: {institute.gstNumber}
                  </p>
                )}
              </div>
            </div>

            <div className="fd-invoice__meta">
              <span className="fd-invoice__title">INVOICE</span>
              <div className="fd-invoice__meta-row">
                <span className="fd-invoice__meta-label">Invoice No.</span>
                <span className="fd-invoice__meta-value">{invoiceNumber}</span>
              </div>
              <div className="fd-invoice__meta-row">
                <span className="fd-invoice__meta-label">Date</span>
                <span className="fd-invoice__meta-value">{formatDate(txn.date || txn.createdAt)}</span>
              </div>
              <div className={`fd-status-badge ${status.cls}`}>
                <StatusIcon size={14} />
                <span>{status.label}</span>
              </div>
            </div>
          </header>

          <div className="fd-divider" />

          {/* Bill To & Course */}
          <section className="fd-grid-2">
            <div className="fd-block">
              <h4 className="fd-block__title">
                <UserIcon size={14} /> Bill To
              </h4>
              <div className="fd-bill-to">
                <UserAvatar name={student?.name || "?"} size="md" />
                <div className="fd-bill-to__details">
                  <span className="fd-bill-to__name">{student?.name || "—"}</span>
                  {student?.email && <span className="fd-bill-to__line">{student.email}</span>}
                  {student?.mobile && <span className="fd-bill-to__line">{student.mobile}</span>}
                  {student?.address && <span className="fd-bill-to__line">{student.address}</span>}
                </div>
              </div>
            </div>

            <div className="fd-block">
              <h4 className="fd-block__title">
                <FileText size={14} /> Course
              </h4>
              <div className="fd-course">
                <span className="fd-course__name">{courseName}</span>
                {batchName && <span className="fd-course__batch">Batch: {batchName}</span>}
                {txn?.courseAssignmentId?.courseId?.courseType === "self-paced" && (
                  <span className="fd-tag">Self-paced</span>
                )}
              </div>
            </div>
          </section>

          {/* Line items table */}
          <section className="fd-block fd-block--items">
            <h4 className="fd-block__title">Payment Details</h4>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead>
                  <tr>
                    <th className="fd-table__desc">Description</th>
                    <th>Mode</th>
                    <th>Reference</th>
                    <th className="fd-table__num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fd-table__desc">
                      <div className="fd-li-desc">
                        <span className="fd-li-desc__title">
                          {txn.comments || `${courseName !== "—" ? courseName + " — " : ""}Fee Payment`}
                        </span>
                        <span className="fd-li-desc__sub">
                          Paid on {formatDate(txn.date || txn.createdAt)}
                          {txn.paidTo ? ` · Received by ${txn.paidTo}` : ""}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`fd-mode-pill${isOnline ? " fd-mode-pill--online" : ""}`}>
                        {txn.paymentMode || "—"}
                      </span>
                    </td>
                    <td>
                      <span className="fd-ref">
                        <Hash size={12} />
                        {txnRef}
                      </span>
                    </td>
                    <td className="fd-table__num">{formatCurrency(txn.currentPayment)}</td>
                  </tr>
                  {refundedAmount > 0 && (
                    <tr className="fd-table__refund-row">
                      <td className="fd-table__desc">
                        <div className="fd-li-desc">
                          <span className="fd-li-desc__title fd-text-refund">Refund</span>
                          {txn.refundReason && (
                            <span className="fd-li-desc__sub">{txn.refundReason}</span>
                          )}
                        </div>
                      </td>
                      <td colSpan={2} className="fd-text-refund">
                        {formatDate(txn.refundedAt)}
                      </td>
                      <td className="fd-table__num fd-text-refund">
                        − {formatCurrency(refundedAmount)}
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="fd-table__sub">
                    <td colSpan={3} className="fd-table__num">Subtotal</td>
                    <td className="fd-table__num">{formatCurrency(txn.currentPayment)}</td>
                  </tr>
                  {refundedAmount > 0 && (
                    <tr>
                      <td colSpan={3} className="fd-table__num">Refunded</td>
                      <td className="fd-table__num fd-text-refund">
                        − {formatCurrency(refundedAmount)}
                      </td>
                    </tr>
                  )}
                  <tr className="fd-table__total">
                    <td colSpan={3} className="fd-table__num">Net Paid</td>
                    <td className="fd-table__num">{formatCurrency(netPaid)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Balance summary */}
          {(student?.totalFees != null || student?.pendingFees != null) && (
            <section className="fd-balance">
              <div className="fd-balance__item">
                <span className="fd-balance__label">Total Course Fees</span>
                <span className="fd-balance__value">{formatCurrency(student?.totalFees)}</span>
              </div>
              <div className="fd-balance__item">
                <span className="fd-balance__label">Total Paid</span>
                <span className="fd-balance__value fd-text-success">
                  {formatCurrency(student?.paidFees)}
                </span>
              </div>
              <div className="fd-balance__item">
                <span className="fd-balance__label">Outstanding</span>
                <span className={`fd-balance__value ${Number(student?.pendingFees) > 0 ? "fd-text-warning" : "fd-text-success"}`}>
                  {formatCurrency(student?.pendingFees)}
                </span>
              </div>
              {txn.nextPaymentDate && (
                <div className="fd-balance__item">
                  <span className="fd-balance__label">
                    <Calendar size={12} /> Next Payment Due
                  </span>
                  <span className="fd-balance__value">{formatDate(txn.nextPaymentDate)}</span>
                </div>
              )}
            </section>
          )}

          {/* Notes & proof */}
          {(txn.comments || txn.refImage) && (
            <section className="fd-grid-2 fd-notes-section">
              {txn.comments && (
                <div className="fd-block">
                  <h4 className="fd-block__title">
                    <MessageSquare size={14} /> Notes
                  </h4>
                  <p className="fd-notes-text">{txn.comments}</p>
                </div>
              )}
              {txn.refImage && (
                <div className="fd-block no-print">
                  <h4 className="fd-block__title">
                    <CreditCard size={14} /> Payment Proof
                  </h4>
                  <button type="button" className="fd-proof-btn" onClick={handleViewProof}>
                    <Eye size={14} /> View attached proof
                  </button>
                </div>
              )}
            </section>
          )}

          <footer className="fd-invoice__footer">
            <p>
              This is a computer-generated invoice and does not require a signature.
            </p>
            <p className="fd-invoice__footer-line">
              {institute?.name}
              {institute?.email && ` · ${institute.email}`}
              {institute?.phone && ` · ${institute.phone}`}
            </p>
          </footer>
        </article>

        {/* Quick actions sidebar — hidden on print */}
        <aside className="fd-side no-print">
          <AppCard>
            <AppCardContent>
              <h4 className="fd-side__title">Quick Actions</h4>
              <div className="fd-side__actions">
                <AppButton variant="secondary" onClick={handlePrint} className="fd-side__btn">
                  <Printer size={16} /> Print Invoice
                </AppButton>
                <AppButton
                  variant="secondary"
                  onClick={handleDownload}
                  disabled={downloading}
                  className="fd-side__btn"
                >
                  {downloading
                    ? <Spinner animation="border" size="sm" />
                    : <Download size={16} />}
                  Download PDF
                </AppButton>
                <AppButton
                  onClick={() => { setSendError(""); setShowSendModal(true); }}
                  className="fd-side__btn"
                >
                  <Mail size={16} /> Send to Email
                </AppButton>
                {student?._id && txn?._id && (
                  <AppButton
                    variant="ghost"
                    onClick={() => navigate(`/students/${student._id}/fees/edit/${txn._id}`)}
                    className="fd-side__btn"
                  >
                    <Pencil size={16} /> Edit Fee
                  </AppButton>
                )}
              </div>

              <div className="fd-side__divider" />

              <h4 className="fd-side__title">Transaction Info</h4>
              <ul className="fd-side__info">
                <li>
                  <span>Status</span>
                  <span className={`fd-status-badge fd-status-badge--sm ${status.cls}`}>
                    <StatusIcon size={12} />
                    {status.label}
                  </span>
                </li>
                <li>
                  <span>Gateway</span>
                  <strong>{isOnline ? "Razorpay" : "Manual"}</strong>
                </li>
                <li>
                  <span>Mode</span>
                  <strong>{txn.paymentMode || "—"}</strong>
                </li>
                <li>
                  <span>Reference</span>
                  <strong className="fd-truncate">{txnRef}</strong>
                </li>
                <li>
                  <span>Created</span>
                  <strong>{formatDate(txn.createdAt, true)}</strong>
                </li>
                {txn.paidTo && (
                  <li>
                    <span>Received By</span>
                    <strong>{txn.paidTo}</strong>
                  </li>
                )}
              </ul>
            </AppCardContent>
          </AppCard>
        </aside>
      </div>

      {/* ── Send invoice modal ── */}
      <AppModal
        show={showSendModal}
        onHide={() => { if (!sending) setShowSendModal(false); }}
        title="Send Invoice via Email"
        icon={<Mail size={18} />}
        size="sm"
        footer={
          <>
            <AppButton
              variant="secondary"
              onClick={() => setShowSendModal(false)}
              disabled={sending}
            >
              Cancel
            </AppButton>
            <AppButton onClick={handleSend} disabled={sending}>
              {sending ? <><Spinner animation="border" size="sm" /> Sending…</> : (<><Mail size={16} /> Send Invoice</>)}
            </AppButton>
          </>
        }
      >
        <div className="fd-send-modal">
          <p className="fd-send-modal__desc">
            The invoice PDF for{" "}
            <strong>{invoiceNumber}</strong>{" "}
            (<strong>{formatCurrency(txn.currentPayment)}</strong>) will be emailed to:
          </p>
          <label className="fd-send-modal__label">
            Recipient Email <span>*</span>
          </label>
          <input
            type="email"
            className="fd-send-modal__input"
            value={emailValue}
            onChange={(e) => { setEmailValue(e.target.value); setSendError(""); }}
            placeholder="student@example.com"
            disabled={sending}
            autoFocus
          />
          <p className="fd-send-modal__hint">
            Defaults to the student's registered email. You can override it.
          </p>
          {sendError && (
            <div className="fd-send-modal__error">
              <AlertCircle size={14} /> {sendError}
            </div>
          )}
        </div>
      </AppModal>

      <FilePreviewModal
        show={showImage}
        onHide={() => { setShowImage(false); setImageUrl(null); }}
        url={imageUrl}
        fileName="Payment Proof"
        loading={imageLoading}
        isImage
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default FeeDetailsPage;
